<template>
  <span class="p-help-modal">
    <button type="button" class="p-help-modal__trigger" @click.prevent="open">
      <CIcon name="cipCircleHelp" size="custom-size" width="16" height="16"/>
    </button>
    <CModal :title="title" :show.sync="isVisible" :close-on-backdrop="true" :size="size" centered>
      <div class="p-help-modal__body">
        <p v-if="intro" class="p-help-modal__intro">{{ intro }}</p>
        <ol v-if="steps.length" class="p-help-modal__steps">
          <li v-for="(step, index) in steps" :key="index" class="p-help-modal__step">
            <span class="p-help-modal__step-number">{{ index + 1 }}</span>
            <span class="p-help-modal__step-content">
              <span class="p-help-modal__step-title">{{ step.title }}</span>
              <span class="p-help-modal__step-text">{{ step.text }}</span>
            </span>
          </li>
        </ol>
        <slot/>
        <div v-if="notes.length" class="p-help-modal__notes">
          <div class="p-help-modal__notes-title">
            <CIcon name="cipInfo" size="sm" class="mr-2"/>
            {{ notesTitleLabel }}
          </div>
          <ul class="p-help-modal__notes-list">
            <li v-for="(note, index) in notes" :key="index">{{ note }}</li>
          </ul>
        </div>
      </div>
      <template #footer>
        <CButton color="primary" @click="close">
          <translate translate-context="help_modal">Got it</translate>
        </CButton>
      </template>
    </CModal>
  </span>
</template>

<script>
export default {
  name: "PHelpModal",
  props: {
    title: {
      type: String,
      required: true
    },
    intro: {
      type: String,
      default: ''
    },
    steps: {
      type: Array,
      default: () => []
    },
    notes: {
      type: Array,
      default: () => []
    },
    notesTitle: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'lg'
    }
  },
  data() {
    return {
      isVisible: false
    }
  },
  computed: {
    notesTitleLabel() {
      return this.notesTitle || this.$pgettext('help_modal', 'Good to know')
    }
  },
  methods: {
    open() {
      this.isVisible = true
    },
    close() {
      this.isVisible = false
    }
  }
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/variables/_variables";

// Brand dark surface, same as app tooltips and popovers
$step-number-bg: $tooltip-bg;

.p-help-modal {
  display: inline-flex;
  vertical-align: text-top;
}

.p-help-modal__trigger {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  color: $gray-800;
  outline: none;

  &:hover {
    color: $primary;
  }
}

.p-help-modal__intro {
  background: $gray-100;
  border: $border-width solid $border-color;
  border-radius: $border-radius;
  padding: 12px 14px;
  margin-bottom: 18px;
  line-height: 1.5;
  color: $gray-900;
}

.p-help-modal__steps {
  list-style: none;
  margin: 0;
  padding: 0;
}

.p-help-modal__step {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
  padding-bottom: 18px;

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 13px;
    top: 28px;
    bottom: 0;
    width: 2px;
    background: $gray-200;
  }

  &:last-child {
    padding-bottom: 0;
  }
}

.p-help-modal__step-number {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: $step-number-bg;
  color: $white;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.p-help-modal__step-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 3px;
}

.p-help-modal__step-title {
  font-weight: 600;
  color: $black;
  line-height: 1.4;
}

.p-help-modal__step-text {
  font-size: 0.88rem;
  color: $gray-900;
  line-height: 1.5;
}

.p-help-modal__notes {
  margin-top: 20px;
  background: $info-25;
  border: $border-width solid $info-50;
  border-radius: $border-radius;
  padding: 12px 14px;
}

.p-help-modal__notes-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: $primary-dark;
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.p-help-modal__notes-list {
  margin: 0;
  padding-left: 18px;
  color: $gray-900;

  li {
    margin-bottom: 6px;
    line-height: 1.45;
    font-size: 0.88rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
